import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, SUPABASE_URL } from '@/lib/supabaseClient';
import { storageAPI } from '../services/localStorage';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [properties, setProperties] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [heroSlides, setHeroSlides] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryCategories, setGalleryCategories] = useState([]);
  const [propertyCategories, setPropertyCategories] = useState([]);
  const [listingTeam, setListingTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const legacyBackendBase =
    process.env.REACT_APP_BACKEND_URL ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  const supabaseStorageBase = SUPABASE_URL
    ? `${SUPABASE_URL.replace(/\/$/, '')}/storage/v1/object/public`
    : null;

  const toAbsolute = (url) => {
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    if (url.startsWith('/storage') && legacyBackendBase) {
      return `${legacyBackendBase}${url}`;
    }
    if (url.startsWith('storage/') && legacyBackendBase) {
      return `${legacyBackendBase}/${url}`;
    }
    if (supabaseStorageBase) {
      return `${supabaseStorageBase}/${url.replace(/^\/+/, '')}`;
    }
    if (legacyBackendBase) {
      return `${legacyBackendBase}/${url.replace(/^\/+/, '')}`;
    }
    return url;
  };

  const fetchList = async (queryPromise) => {
    const { data, error: supaError } = await queryPromise;
    if (supaError) throw supaError;
    return data || [];
  };

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const responses = await Promise.allSettled([
        fetchList(
          supabase
            .from('properties')
            .select('*')
            .order('created_at', { ascending: false })
        ),
        fetchList(
          supabase
            .from('apartments')
            .select('*')
            .order('created_at', { ascending: false })
        ),
        fetchList(
          supabase
            .from('blog_posts')
            .select('*')
            .eq('published', true)
            .order('date', { ascending: false })
        ),
        fetchList(
          supabase
            .from('hero_slides')
            .select('*')
            .order('order', { ascending: true })
        ),
        fetchList(
          supabase
            .from('testimonials')
            .select('*')
            .order('created_at', { ascending: false })
        ),
        fetchList(
          supabase
            .from('gallery_images')
            .select('*, gallery_categories(name)')
            .order('display_order', { ascending: true })
        ),
        fetchList(
          supabase
            .from('gallery_categories')
            .select('*')
            .eq('is_active', true)
            .order('display_order', { ascending: true })
        ),
        fetchList(
          supabase
            .from('property_categories')
            .select('*')
            .eq('is_active', true)
            .order('display_order', { ascending: true })
        ),
        fetchList(
          supabase
            .from('listing_team')
            .select('*')
            .eq('is_active', true)
            .order('display_order', { ascending: true })
        ),
      ]);

      const [
        propertiesRes,
        apartmentsRes,
        blogsRes,
        slidesRes,
        testimonialsRes,
        galleryImagesRes,
        galleryCategoriesRes,
        propertyCategoriesRes,
        listingTeamRes,
      ] = responses;

      const normalizeProperty = (p) => {
        const image = p.image ? toAbsolute(p.image) : null;
        const gallery = Array.isArray(p.gallery) ? p.gallery.map(toAbsolute) : (p.gallery ? [toAbsolute(p.gallery)] : []);
        return {
          ...p,
          // compatibility fields used by several components
          featured_image: p.featured_image ? toAbsolute(p.featured_image) : image,
          images: Array.isArray(p.images) ? p.images.map(toAbsolute) : (gallery && gallery.length ? gallery : (image ? [image] : [])),
          image,
          gallery,
        };
      };

      const normalizeApartment = (apt) => ({
        ...apt,
        hero_image: toAbsolute(apt.hero_image),
        featured_image: toAbsolute(apt.featured_image),
        images: Array.isArray(apt.images)
          ? apt.images.map(toAbsolute)
          : apt.images
          ? [toAbsolute(apt.images)]
          : [],
      });

      const normalizeBlog = (blog) => ({
        ...blog,
        image: toAbsolute(blog.image),
      });

      const normalizeSlide = (slide) => ({
        ...slide,
        background_image: toAbsolute(slide.background_image),
        interior_image: toAbsolute(slide.interior_image),
      });

      const normalizeTestimonial = (testimonial) => ({
        ...testimonial,
        avatar_url: toAbsolute(testimonial.avatar_url),
      });

      const unwrap = (result) => (result.status === 'fulfilled' ? result.value : []);

      const rawProps = unwrap(propertiesRes);
      setProperties(Array.isArray(rawProps) ? rawProps.map(normalizeProperty) : []);

      const rawApartments = unwrap(apartmentsRes);
      setApartments(Array.isArray(rawApartments) ? rawApartments.map(normalizeApartment) : []);

      const rawBlogs = unwrap(blogsRes);
      setBlogPosts(Array.isArray(rawBlogs) ? rawBlogs.map(normalizeBlog) : []);

      let slides = unwrap(slidesRes);
      slides = Array.isArray(slides) ? slides.map(normalizeSlide) : [];

      // If API returned no slides, fall back to localStorage default slides
      if (!slides || slides.length === 0) {
        try {
          const localSlides = storageAPI.getHeroSlides();
          if (localSlides && localSlides.length > 0) {
            slides = localSlides;
          }
        } catch (err) {
          // ignore localStorage failures
        }
      }
      setHeroSlides(slides);

      const rawTestimonials = unwrap(testimonialsRes);
      setTestimonials(Array.isArray(rawTestimonials) ? rawTestimonials.map(normalizeTestimonial) : []);

      const rawGalleryImages = unwrap(galleryImagesRes);
      const normalizeGalleryImage = (img) => ({
        ...img,
        images: Array.isArray(img.images) ? img.images.map(toAbsolute) : (img.image_url ? [toAbsolute(img.image_url)] : []),
        image_url: toAbsolute(img.image_url),
        category_name: img.gallery_categories?.name || 'Uncategorized'
      });
      setGalleryImages(Array.isArray(rawGalleryImages) ? rawGalleryImages.map(normalizeGalleryImage) : []);

      const rawGalleryCategories = unwrap(galleryCategoriesRes);
      setGalleryCategories(Array.isArray(rawGalleryCategories) ? rawGalleryCategories : []);

      const rawPropertyCategories = unwrap(propertyCategoriesRes);
      setPropertyCategories(Array.isArray(rawPropertyCategories) ? rawPropertyCategories : []);

      const rawListingTeam = unwrap(listingTeamRes);
      const normalizeListingMember = (member) => ({
        ...member,
        avatar_url: toAbsolute(member.avatar_url),
        image_url: toAbsolute(member.image_url),
        photo: toAbsolute(member.photo),
      });
      setListingTeam(Array.isArray(rawListingTeam) ? rawListingTeam.map(normalizeListingMember) : []);

      const failureCount = responses.filter((res) => res.status === 'rejected').length;
      if (failureCount === responses.length) {
        setError('Unable to load content from Supabase.');
      } else if (failureCount > 0) {
        setError('Some content could not be loaded. Please retry.');
      } else {
        setError(null);
      }
      
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();

    const handler = (e) => {
      // when a resource changes in the dashboard, refresh data
      try {
        // optional: inspect e.detail.table to selectively refresh
        fetchAllData();
      } catch (err) {
        // ignore
      }
    };

    window.addEventListener('resourceUpdated', handler);
    window.addEventListener('resourceDeleted', handler);

    return () => {
      window.removeEventListener('resourceUpdated', handler);
      window.removeEventListener('resourceDeleted', handler);
    };
  }, []);

  // Methods for dashboard to update data
  const refreshData = () => {
    fetchAllData();
  };

  const addProperty = (property) => {
    setProperties(prev => [property, ...prev]);
  };

  const updateProperty = (updatedProperty) => {
    setProperties(prev => 
      prev.map(p => p.id === updatedProperty.id ? updatedProperty : p)
    );
  };

  const removeProperty = (propertyId) => {
    setProperties(prev => prev.filter(p => p.id !== propertyId));
  };

  const addBlogPost = (blogPost) => {
    setBlogPosts(prev => [blogPost, ...prev]);
  };

  const updateBlogPost = (updatedBlogPost) => {
    setBlogPosts(prev => 
      prev.map(b => b.id === updatedBlogPost.id ? updatedBlogPost : b)
    );
  };

  const removeBlogPost = (blogPostId) => {
    setBlogPosts(prev => prev.filter(b => b.id !== blogPostId));
  };

  const addHeroSlide = (slide) => {
    setHeroSlides(prev => [...prev, slide]);
  };

  const updateHeroSlide = (slideId, updatedSlide) => {
    setHeroSlides(prev => prev.map(s => s.id === slideId ? { ...s, ...updatedSlide } : s));
  };

  const removeHeroSlide = (slideId) => {
    setHeroSlides(prev => prev.filter(s => s.id !== slideId));
  };

  const value = {
    // Data
    properties,
    apartments: Array.isArray(apartments) && apartments.length
      ? apartments
      : Array.isArray(properties)
      ? properties
      : [],
    blogPosts,
    testimonials,
    heroSlides,
    galleryImages,
    galleryCategories,
    propertyCategories,
    listingTeam,
    loading,
    error,
    
    // Computed
    featuredProperties: Array.isArray(properties)
      ? properties.filter(p => p.status === 'available').slice(0, 6)
      : [],
    featuredBlogPosts: Array.isArray(blogPosts)
      ? blogPosts.filter(b => b.published).slice(0, 3)
      : [],
    
    // Methods
    addProperty,
    updateProperty,
    removeProperty,
    addBlogPost,
    updateBlogPost,
    removeBlogPost,
    addHeroSlide,
    updateHeroSlide,
    removeHeroSlide,
    refreshData
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};
